<template>
  <PopoverRoot v-model:open="isOpen">
    <PopoverTrigger as-child>
      <slot name="trigger"></slot>
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        align="start"
        :side-offset="8"
        :collision-padding="10"
        class="z-20 flex flex-col rounded-md border border-transparent bg-gray-900 text-white shadow-lg transition dark:border-border dark:bg-sidebar"
        :class="[$style.popover_content, !narrow && `w-80`]"
      >
        <ListboxRoot
          :model-value="selection"
          multiple
          class="contents"
          @update:model-value="selection = Array.isArray($event) ? $event : []"
        >
          <div v-if="query !== undefined" class="p-2 pb-0">
            <div
              class="relative flex h-8 items-center rounded border border-gray-600 bg-gray-800 focus-within:border-blue-500 dark:border-border dark:bg-sidebar"
            >
              <div class="grid size-8 place-items-center">
                <MagnifyingGlassIcon class="size-4 text-muted-foreground" />
              </div>

              <ListboxFilter
                ref="inputRef"
                v-model="query"
                placeholder="Search"
                class="peer h-full grow border-0 bg-transparent px-0 text-sm placeholder:text-muted-foreground focus:ring-0"
              />

              <div class="flex items-center pr-2">
                <div
                  v-if="maxSelection !== undefined && maxSelection > 1"
                  class="ml-2 select-none text-xs"
                  :class="[
                    selection.length >= maxSelection
                      ? `text-red-300`
                      : `text-gray-300`,
                  ]"
                >
                  {{ selection.length }}/{{ maxSelection }}
                </div>

                <div
                  v-if="isPending"
                  class="-mr-2 grid size-8 place-items-center"
                >
                  <ArrowPathIcon class="size-4 animate-spin text-gray-400" />
                </div>

                <button
                  v-if="query"
                  type="button"
                  aria-label="Clear"
                  class="-mr-2 grid size-8 place-items-center text-gray-400 transition-colors hover:text-gray-200 dark:hover:text-white"
                  @click="query = ''"
                >
                  <XMarkIcon class="size-4" />
                </button>
              </div>
            </div>
          </div>

          <ListboxContent class="overflow-y-auto py-2">
            <slot v-if="options.length === 0" name="empty-state">
              <div class="flex h-20 flex-col items-center justify-center">
                <p class="text-sm text-gray-400">No items</p>
              </div>
            </slot>

            <ListboxGroup
              v-for="(children, groupName, idx) in groupedOptions"
              :key="groupName"
            >
              <hr
                v-if="idx !== 0"
                class="my-2 border-t border-gray-700 dark:border-border"
              />

              <ListboxGroupLabel
                v-if="groupName"
                class="mx-5 my-1 shrink-0 select-none border-t-border text-[0.8125rem] leading-5 text-gray-400 transition dark:border-border dark:text-muted-foreground"
              >
                {{ groupName }}
              </ListboxGroupLabel>

              <ListboxItem
                v-for="opt in children"
                :key="opt.id"
                :value="opt.id"
                :disabled="opt.disabled || isOptionDisabled(opt.id)"
                class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm transition-all duration-75 data-[highlighted]:bg-gray-700 dark:data-[highlighted]:bg-muted"
                :class="[
                  query === undefined && `outline-none hover:bg-dark-muted`,
                ]"
                @select="inputRef?.$el.focus()"
              >
                <component
                  :is="opt.icon"
                  class="mt-0.5 size-4 shrink-0 text-muted-foreground transition-colors"
                />

                <span
                  class="min-w-0 grow whitespace-nowrap break-words transition group-data-[disabled]:opacity-50"
                >
                  {{ opt.label }}
                </span>

                <CheckIcon
                  v-if="opt.id !== '' || !opt.disabled"
                  class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition duration-75 group-data-[state=checked]:opacity-100"
                />
              </ListboxItem>
            </ListboxGroup>
          </ListboxContent>
        </ListboxRoot>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts" generic="T extends string = string">
import {
  ListboxContent,
  ListboxFilter,
  ListboxGroup,
  ListboxGroupLabel,
  ListboxItem,
  ListboxRoot,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { type Component, computed, onMounted, ref, toRef } from "vue";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckIcon from "@/icons/heroicons/check-micro.svg";
import MagnifyingGlassIcon from "@/icons/heroicons/magnifying-glass-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import { useStablePending } from "@/lib/composables/stable-pending";

export interface SelectOption<T extends string = string> {
  id: T;
  icon?: Component;
  label: string;
  disabled?: boolean;
  group?: string;
}

const inputRef = ref<{ $el: HTMLInputElement }>();

const props = defineProps<{
  options: readonly SelectOption<T>[];
  query?: string;
  pending?: boolean;
  narrow?: boolean;
  maxSelection?: number;
  id?: string;
  closeOnSelect?: boolean;
}>();

const emit = defineEmits<{
  "update:query": [next: string];
}>();

const selection = defineModel<T[]>("selection", {
  default: [],
  set(value) {
    if (props.closeOnSelect) {
      isOpen.value = false;
    }

    return value;
  },
});
const query = computed({
  get(): string | undefined {
    return props.query;
  },
  set(next: string) {
    emit("update:query", next);
  },
});

const isPending = useStablePending(toRef(props, "pending"));
const isOpen = ref(false);

onMounted(() => {
  // If this element is emphasized, open the menu.
  const emphasisQueryParameter =
    new URLSearchParams(window.location.search).get("emphasis") === props.id;
  if (emphasisQueryParameter) {
    isOpen.value = true;
  }
});

const isOptionDisabled = (id: T) => {
  if (props.maxSelection === 1) {
    return false;
  }
  return (
    props.maxSelection !== undefined &&
    selection.value.length >= props.maxSelection &&
    !selection.value.includes(id)
  );
};

const groupedOptions = computed(() => {
  const ungroupedOptions: SelectOption<T>[] = [];
  const groups: Record<string, SelectOption<T>[]> = {};

  for (const option of props.options) {
    const groupName = option.group ?? "";

    if (groupName === "") {
      ungroupedOptions.push(option);
      continue;
    }

    const group = (groups[groupName] ??= []);
    group.push(option);
  }

  // Always sort ungrouped options first
  return ungroupedOptions.length ? { "": ungroupedOptions, ...groups } : groups;
});
</script>

<style module>
.popover_content {
  max-height: min(80dvh, var(--reka-popover-content-available-height));
  max-width: min(320px, var(--reka-popover-content-available-width));
  transform-origin: var(--reka-popover-content-transform-origin);

  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] :global {
      animation: pop-in 250ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: pop-out 250ms var(--motion-timing-ease);
    }
  }
}
</style>
